/* eslint-disable promise/always-return */
/* eslint-disable promise/catch-or-return */
/**
 * Package: svgedit.sanitize
 *
 * Licensed under the MIT License
 *
 * Copyright(c) 2010 Alexis Deveria
 * Copyright(c) 2010 Jeff Schiller
 */

// Dependencies:
// 1) jQuery
// 2) browser.js
// 3) svgutils.js
import { constants as config } from '@config';
import QRCodeCreator from 'assets/js/editor/facade/design/creators/qrcode';
import { sanitizeDimensionAttributes } from 'src/utils/sanitize/dimensions-atributes';

(function () {
  'use strict';

  if (!svgedit.sanitize) {
    svgedit.sanitize = {};
  }

  svgedit.sanitize._fontLoaders = {};

  const NS = svgedit.NS;
  const REVERSE_NS = svgedit.getReverseNS();

  const { userInput, userInputSmartAsset, definedAsset, definedContentType } = config.smartAssets.attributes;
  const { cmykFill, cmykStroke } = config.cmykAttributes;
  const qrCodeAttributes = QRCodeCreator.getAllowedAttributes();
  const tableRowAttributes = ['data-min-content-height', 'data-min-content-width'];

  // this defines which elements and attributes that we support
  const svgWhiteList_ = {
    // SVG Elements
    a: ['attribution', 'class', 'data-meta', 'clip-path', 'clip-rule', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'id', 'mask', 'opacity', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'xlink:href', 'xlink:title', 'data-duration', cmykFill, cmykStroke],
    circle: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'cx', 'cy', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'id', 'mask', 'opacity', 'r', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'data-bounding-x', 'data-bounding-y', 'data-bounding-width', 'data-bounding-height', cmykFill, cmykStroke],
    clipPath: ['class', 'clipPathUnits', 'id'],
    defs: [],
    style: ['type'],
    desc: [],
    ellipse: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'cx', 'cy', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'id', 'mask', 'opacity', 'requiredFeatures', 'rx', 'ry', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'data-bounding-x', 'data-bounding-y', 'data-bounding-width', 'data-bounding-height', cmykFill, cmykStroke],
    foreignObject: ['class', 'data-element-restriction-id', 'font-size', 'height', 'id', 'opacity', 'requiredFeatures', 'style', 'transform', 'width', 'x', 'y'],
    g: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-meta', 'data-heading', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'id', 'display', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'mask', 'opacity', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'custom-font-owner', 'custom-font-id', 'font-stretch', 'font-family', 'font-size', 'font-style', 'font-weight', 'text-anchor', 'text-decoration', 'data-vertical-offset', 'data-paragraph-dy', 'data-first-line-margin', 'data-right-margin', 'data-dy', 'data-margin', 'direction', 'data-bounding-x', 'data-bounding-y', 'data-bounding-width', 'data-bounding-height', 'highlight', 'data-parent', 'data-child', 'font-variant', 'data-ordered-start', 'data-columns-gap', 'data-balance-columns', cmykFill, cmykStroke, 'data-spacing', 'data-paragraph-style', 'data-duration', ...qrCodeAttributes, ...tableRowAttributes],
    image: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'filter', 'height', 'id', 'mask', 'opacity', 'requiredFeatures', 'style', 'systemLanguage', 'transform', 'width', 'x', 'xlink:href', 'xlink:title', 'y', 'data-background-removed', 'data-src', 'data-duration', 'data-hash'],
    line: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-meta', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'id', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'opacity', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'x1', 'x2', 'y1', 'y2', 'data-bounding-x', 'data-bounding-y', 'data-bounding-width', 'data-bounding-height', cmykFill, cmykStroke],
    linearGradient: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-element-restriction-id', 'id', 'gradientTransform', 'gradientUnits', 'requiredFeatures', 'spreadMethod', 'systemLanguage', 'x1', 'x2', 'xlink:href', 'y1', 'y2'],
    marker: ['id', 'class', 'data-element-restriction-id', 'markerHeight', 'markerUnits', 'markerWidth', 'orient', 'preserveAspectRatio', 'refX', 'refY', 'systemLanguage', 'viewBox'],
    mask: ['class', 'height', 'id', 'maskContentUnits', 'maskUnits', 'width', 'x', 'y'],
    metadata: ['class', 'id'],
    path: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'd', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'id', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'opacity', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'data-bounding-x', 'data-bounding-y', 'data-bounding-width', 'data-bounding-height', cmykFill, cmykStroke],
    pattern: ['class', 'data-element-restriction-id', 'height', 'id', 'patternContentUnits', 'patternTransform', 'patternUnits', 'requiredFeatures', 'style', 'systemLanguage', 'viewBox', 'width', 'x', 'xlink:href', 'y'],
    polygon: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'id', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'id', 'class', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'opacity', 'points', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', cmykFill, cmykStroke],
    polyline: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'id', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'opacity', 'points', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', cmykFill, cmykStroke],
    radialGradient: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-element-restriction-id', 'cx', 'cy', 'fx', 'fy', 'gradientTransform', 'gradientUnits', 'id', 'r', 'requiredFeatures', 'spreadMethod', 'systemLanguage', 'xlink:href'],
    rect: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-size', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'height', 'id', 'mask', 'opacity', 'requiredFeatures', 'rx', 'ry', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'width', 'x', 'y', 'data-bounding-x', 'data-bounding-y', 'data-bounding-width', 'data-bounding-height', cmykFill, cmykStroke],
    stop: ['class', 'id', 'offset', 'requiredFeatures', 'stop-color', 'stop-opacity', 'style', 'systemLanguage'],
    svg: ['class', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'filter', 'id', 'height', 'mask', 'preserveAspectRatio', 'requiredFeatures', 'style', 'systemLanguage', 'viewBox', 'width', 'x', 'xmlns', 'xmlns:se', 'xmlns:xlink', 'y', 'data-bleed-size', 'data-bleed-type', 'data-bleed-unit', 'data-slug-size', 'data-slug-type', 'data-slug-unit'],
    switch: ['class', 'id', 'requiredFeatures', 'systemLanguage'],
    symbol: ['class', 'data-element-restriction-id', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'font-family', 'font-size', 'font-style', 'font-weight', 'id', 'opacity', 'preserveAspectRatio', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'viewBox', cmykFill, cmykStroke],
    text: ['class', 'data-heading', 'data-meta', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'custom-font-owner', 'custom-font-id', 'font-stretch', 'font-family', 'font-size', 'font-style', 'font-weight', 'id', 'mask', 'opacity', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'text-anchor', 'transform', 'dx', 'dy', 'x', 'xml:space', 'y', 'word-spacing', 'letter-spacing', 'kerning', 'textLength', 'lengthAdjust', 'text-decoration', 'highlight', 'data-ordered', 'data-ordered-start', 'data-bullet', 'data-ordered-style', 'data-bullet-style', 'data-ordered-numbering-style', 'data-ordered-regex', cmykFill, cmykStroke],
    textPath: ['class', 'data-element-restriction-id', 'id', 'method', 'requiredFeatures', 'spacing', 'startOffset', 'style', 'systemLanguage', 'transform', 'xlink:href'],
    title: ['class'],
    tspan: [userInput, userInputSmartAsset, definedAsset, definedContentType, 'class', 'data-element-restriction-id', 'clip-path', 'data-meta', 'clip-rule', 'dx', 'dy', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'custom-font-owner', 'custom-font-id', 'font-stretch', 'font-family', 'font-size', 'font-style', 'font-weight', 'id', 'mask', 'opacity', 'requiredFeatures', 'rotate', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'text-anchor', 'textLength', 'transform', 'x', 'xml:space', 'y', 'text-decoration', 'highlight', 'baseline-shift', 'data-ordered-width', 'data-bullet-width', 'data-paragraph-style', cmykFill, cmykStroke],
    use: ['class', 'data-element-restriction-id', 'clip-path', 'clip-rule', 'fill', 'data-color-group', 'fill-opacity', 'fill-rule', 'filter', 'height', 'id', 'mask', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'transform', 'width', 'x', 'xlink:href', 'y', cmykFill],

    // Filters Elements
    filter: ['class', 'color-interpolation-filters', 'filterRes', 'filterUnits', 'height', 'id', 'primitiveUnits', 'requiredFeatures', 'width', 'x', 'xlink:href', 'y'],
    feGaussianBlur: ['class', 'color-interpolation-filters', 'id', 'requiredFeatures', 'stdDeviation'],
    feColorMatrix: ['values', 'type'],
    feFuncR: ['intercept', 'type', 'slope'],
    feFuncG: ['intercept', 'type', 'slope'],
    feFuncB: ['intercept', 'type', 'slope'],
    feComponentTransfer: [],
    feConvolveMatrix: ['order', 'preserveAlpha', 'kernelMatrix'],
    feFlood: ['result', 'x', 'y', 'width', 'height', 'flood-color', 'flood-opacity'],
    fePointLight: ['x', 'y', 'z'],
    feSpecularLighting: ['result', 'lighting-color', 'surfaceScale', 'specularConstant', 'specularExponent'],
    feBlend: ['result', 'in', 'in2', 'mode'],

    // MathML Elements
    annotation: ['encoding'],
    'annotation-xml': ['encoding'],
    maction: ['actiontype', 'other', 'selection'],
    math: ['class', 'id', 'display', 'xmlns'],
    menclose: ['notation'],
    merror: [],
    mfrac: ['linethickness'],
    mi: ['mathvariant'],
    mmultiscripts: [],
    mn: [],
    mo: ['fence', 'lspace', 'maxsize', 'minsize', 'rspace', 'stretchy'],
    mover: [],
    mpadded: ['lspace', 'width', 'height', 'depth', 'voffset'],
    mphantom: [],
    mprescripts: [],
    mroot: [],
    mrow: ['xlink:href', 'xlink:type', 'xmlns:xlink'],
    mspace: ['depth', 'height', 'width'],
    msqrt: [],
    mstyle: ['displaystyle', 'mathbackground', 'mathcolor', 'mathvariant', 'scriptlevel'],
    msub: [],
    msubsup: [],
    msup: [],
    mtable: ['align', 'columnalign', 'columnlines', 'columnspacing', 'displaystyle', 'equalcolumns', 'equalrows', 'frame', 'rowalign', 'rowlines', 'rowspacing', 'width'],
    mtd: ['columnalign', 'columnspan', 'rowalign', 'rowspan'],
    mtext: [],
    mtr: ['columnalign', 'rowalign'],
    munder: [],
    munderover: [],
    none: [],
    semantics: []
  };

  // Produce a Namespace-aware version of svgWhitelist
  const svgWhiteListNS_ = {};
  $.each(svgWhiteList_, function (elt, atts) {
    const attNS = {};
    $.each(atts, function (i, att) {
      if (att.indexOf(':') >= 0) {
        const v = att.split(':');
        attNS[v[1]] = NS[(v[0]).toUpperCase()];
      } else {
        attNS[att] = att === 'xmlns' ? NS.XMLNS : null;
      }
    });
    svgWhiteListNS_[elt] = attNS;
  });

  svgedit.sanitize.getAttributeName = function (node, attr) {
    const allowedAttrs = svgWhiteList_[node.nodeName];
    const attrName = attr.nodeName;
    const attrLocalName = attr.localName.toLowerCase();

    let retval = attrName;
    _.any(allowedAttrs, function (attr) {
      if (attr.toLowerCase() === attrLocalName) {
        retval = attr;
        return true;
      }
    });

    return retval;
  };

  // Function: svgedit.sanitize.sanitizeSvg
  // Sanitizes the input node and its children
  // It only keeps what is allowed from our whitelist defined above
  //
  // Parameters:
  // node - The DOM element to be checked (we'll also check its children)
  svgedit.sanitize.sanitizeSvg = function (node) {
    function recalculateText (node) {
      let loadedFonts = 0;

      if (!$.contains(document, node)) {
        _.debounce(_.partial(recalculateText, node), 200);
      } else {
        const $familyHolders = $(node).find('*').filter('[font-family]');

        $familyHolders.each(function () {
          let fontName = String($(this).attr('font-family'));
          const fontStyle = $(this).css('font-style');
          const knownFont = INKIVE.FontsFactory.getSanitizedFontFamily(fontName);

          if (fontName !== knownFont) {
            fontName = knownFont;
            $(this).attr('font-family', knownFont);
          }

          if (!svgedit.sanitize._fontLoaders[fontName]) {
            svgedit.sanitize._fontLoaders[fontName] = getFontLoader(fontName, fontStyle);
            INKIVE.FontsFactory.fontListLoaded
              ? INKIVE.FontsFactory.addSystemFontCSS(fontName)
              : INKIVE.EditorFacade.once('fontlist.loaded', () => { INKIVE.FontsFactory.addSystemFontCSS(fontName); });
          }

          svgedit.sanitize._fontLoaders[fontName].then(function () {
            loadedFonts += 1;
          });
        });
      }

      function getFontLoader (fontName, style) {
        return new Promise(function (resolve, reject) {
          if (fontName === 'Lato' || fontName === 'Nunito') {
            resolve(fontName);
          } else {
            $.fontSpy(fontName, {
              callback: function () {
                resolve(fontName);
              },
              style
            });
          }
        });
      }
    }

    // reverting code to remove empy nodes
    node.nodeType === 1 &&
    $(node).is('.textarea') &&
    !$(node).parents().is('.table-cell') &&
    recalculateText(node);

    // Cleanup text nodes
    if (node.nodeType === 3) { // 3 == TEXT_NODE
      // Trim whitespace
      // NOTE: It is not working with text edition.
      //       see fix in svgcanvas.svgToString()
      if (node.nodeValue !== String.fromCharCode(160) && !/\S/.test(node.nodeValue) && node?.parentElement?.tagName !== 'tspan') {
        node.nodeValue = node.nodeValue.replace(/^\s+|\s+$/g, '');
      }

      // Remove if empty
      if (node.nodeValue.length === 0) {
        node.parentNode.removeChild(node);
        return;
      }
    }

    // We only care about element nodes.
    // Automatically return for all non-element nodes, such as comments, etc.
    if (node.nodeType !== 1) { // 1 == ELEMENT_NODE
      return;
    }

    const doc = node.ownerDocument;
    const parent = node.parentNode;
    // can parent ever be null here?  I think the root node's parent is the document...
    if (!doc || !parent) {
      return;
    }

    const allowedAttrs = svgWhiteList_[node.nodeName];
    const allowedAttrsNS = svgWhiteListNS_[node.nodeName];
    let i;

    // if this element is supported, sanitize it
    if (typeof allowedAttrs !== 'undefined') {
      const seAttrs = [];
      i = node.attributes.length;
      while (i--) {
        // if the attribute is not in our whitelist, then remove it
        // could use jQuery's inArray(), but I don't know if that's any better
        const attr = node.attributes.item(i);
        const attrName = attr.nodeName;
        const attrLocalName = attr.localName.toLowerCase();
        const attrNsURI = attr.namespaceURI;
        const attrVal = attr.nodeValue;

        // Check that an attribute with the correct localName in the correct namespace is on
        // our whitelist or is a namespace declaration for one of our allowed namespaces
        if (!(Object.prototype.hasOwnProperty.call(allowedAttrsNS, attrLocalName) &&
          attrNsURI === allowedAttrsNS[attrLocalName] && attrNsURI !== NS.XMLNS) &&
          !(attrNsURI === NS.XMLNS && REVERSE_NS[attrVal])) {
          // TODO(codedread): Programmatically add the se: attributes to the NS-aware whitelist.
          // Bypassing the whitelist to allow se: prefixes.
          // Is there a more appropriate way to do this?
          if (attrName.indexOf('se:') === 0) {
            seAttrs.push([attrName, attrVal]);
          }

          node.removeAttributeNS(attrNsURI, attrLocalName);
        }

        // Add spaces before negative signs where necessary
        if (svgedit.browser.isGecko()) {
          switch (attrName) {
            case 'transform':
            case 'gradientTransform':
            case 'patternTransform':
              node.setAttribute(attrName, attrVal.replace(/(\d)-/g, '$1 -'));
              break;
          }
        }

        // For the style attribute, rewrite it in terms of XML presentational attributes
        if (attrName === 'style') {
          const props = attrVal.split(';');
          let p = props.length;
          while (p--) {
            const nv = props[p].split(':');
            const styleAttrName = $.trim(nv[0]);
            const styleAttrVal = $.trim(nv[1]);
            // Now check that this attribute is supported
            if (allowedAttrs.indexOf(styleAttrName) >= 0) {
              node.setAttribute(styleAttrName, styleAttrVal);
            }
          }
          svgedit.sanitize.sanitizeSvgStyle(node);
        }

        if (_.includes(['fill', 'stroke'], attrName)) {
          const color = INKIVE.TinyColor(attrVal);
          color.getFormat() === 'rgb' && node.setAttribute(attrName, color.toHexString());
        }
      }

      $.each(seAttrs, function (i, attr) {
        node.setAttributeNS(NS.SE, attr[0], attr[1]);
      });

      // for some elements that have a xlink:href, ensure the URI refers to a local element
      // (but not for links)
      const href = svgedit.utilities.getHref(node);
      if (href &&
        ['filter', 'linearGradient', 'pattern', 'radialGradient', 'textPath', 'use'].indexOf(node.nodeName) >= 0) {
        // TODO: we simply check if the first character is a #, is this bullet-proof?
        if (href[0] !== '#') {
          // remove the attribute (but keep the element)
          svgedit.utilities.setHref(node, '');
          node.removeAttributeNS(NS.XLINK, 'href');
        }
      }

      // Safari crashes on a <use> without a xlink:href, so we just remove the node here
      if (node.nodeName === 'use' && !svgedit.utilities.getHref(node)) {
        parent.removeChild(node);
        return;
      }

      // if the element has attributes pointing to a non-local reference,
      // need to remove the attribute
      $.each(['clip-path', 'fill', 'filter', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'stroke'],
        function (i, attr) {
          let val = node.getAttribute(attr);
          if (val) {
            val = svgedit.utilities.getUrlFromAttr(val);
            // simply check for first character being a '#'
            if (val && val[0] !== '#') {
              node.setAttribute(attr, '');
              node.removeAttribute(attr);
            }
          }
        });

      if ($(node).prop('tagName') === 'image') {
        sanitizeDimensionAttributes(node);
        const content = $(node).html();
        content && $(node).html(content.replace(/&nbsp;/g, ' '));
      } else if ($(node).is('.group') && !$(node).is('a')) {
        const ungroup = _.include([INKIVE.C.BOOK_TYPE.TEMPLATE, INKIVE.C.BOOK_TYPE.RESTRICTED_DESIGN],
          INKIVE.C.BOOK.book_type);

        ungroup && INKIVE.EditorFacade.once('page.move_to', function () {
          INKIVE.DesignFacade.handle(node.id, 'ungroup');
        });
      }

      // recurse to children
      i = node.childNodes.length;
      while (i--) { svgedit.sanitize.sanitizeSvg(node.childNodes.item(i)); }
    } else { // else (element not supported), remove it
      // remove all children from this node and insert them before this node
      // FIXME: in the case of animation elements this will hardly ever be correct
      const children = [];
      while (node.hasChildNodes()) {
        children.push(parent.insertBefore(node.firstChild, node));
      }

      // remove this node from the document altogether
      parent.removeChild(node);

      // call sanitizeSvg on each of those children
      i = children.length;
      while (i--) { svgedit.sanitize.sanitizeSvg(children[i]); }
    }
  };

  svgedit.sanitize.sanitizeSvgColor = element => {
    const sanitizeColor = attribute => node => {
      const color = INKIVE.TinyColor(node.getAttribute(attribute));

      if (color.isValid() && color.getFormat() !== 'hex') {
        node.setAttribute(attribute, color.toHexString());
      }
    };

    return ['fill', 'stroke'].forEach(attribute =>
      element.querySelectorAll(`[${attribute}]`)
        .forEach(sanitizeColor(attribute))
    );
  };

  svgedit.sanitize.sanitizeSvgStyle = element => {
    const validStyles = ['mix-blend-mode'];
    const style = element.style;

    if (!style) return;

    const sanitizedStyle = {};

    for (let i = 0; i < style.length; i++) {
      const key = style[i];
      if (validStyles.includes(key)) {
        sanitizedStyle[key] = style[key];
      }
    }

    for (const key of style) {
      if (!sanitizedStyle[key]) {
        style.removeProperty(key);
      }
    }

    if (Object.keys(sanitizedStyle).length === 0) {
      element.removeAttribute('style');
    } else {
      for (const [key, value] of Object.entries(sanitizedStyle)) {
        style.setProperty(key, value);
      }
    }
  };
}());
